<template>
  <!-- Minimized Call Bar -->
  <div 
    v-if="isMinimized && callActive"
    class="tw-fixed tw-bottom-4 tw-right-4 tw-z-40 tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-shadow-lg tw-p-3 tw-max-w-sm"
  >
    <div class="tw-flex tw-items-center tw-gap-3">
      <!-- Call Indicator -->
      <div class="tw-flex tw-items-center tw-gap-2">
        <div class="tw-w-3 tw-h-3 tw-bg-green-500 tw-rounded-full tw-animate-pulse"></div>
        <icon 
          :icon="callType === 'video' ? 'solar:videocamera-bold-duotone' : 'solar:phone-calling-bold-duotone'" 
          :class="[
            'tw-text-lg',
            callType === 'video' ? 'tw-text-blue-500' : 'tw-text-green-500'
          ]" 
        />
      </div>

      <!-- Call Info -->
      <div class="tw-flex-1 tw-min-w-0">
        <p class="tw-text-sm tw-font-medium tw-text-gray-900 tw-truncate">
          {{ otherParticipant?.name || 'Video Call' }}
        </p>
        <p class="tw-text-xs tw-text-gray-500">
          {{ callDuration || '00:00' }}
        </p>
      </div>

      <!-- Action Buttons -->
      <div class="tw-flex tw-items-center tw-gap-1">
        <!-- Restore Button -->
        <button
          @click="restoreCall"
          class="tw-p-1.5 tw-text-gray-500 hover:tw-text-gray-700 hover:tw-bg-gray-100 tw-rounded tw-transition-colors"
          title="Restore call"
        >
          <icon icon="solar:maximize-bold-duotone" class="tw-text-sm" />
        </button>

        <!-- End Call Button -->
        <button
          @click="endCall"
          class="tw-p-1.5 tw-bg-red-500 hover:tw-bg-red-600 tw-text-white tw-rounded tw-transition-colors"
          title="End call"
        >
          <icon icon="solar:phone-calling-bold-duotone" class="tw-text-sm tw-rotate-180" />
        </button>
      </div>
    </div>
  </div>

  <!-- Call Status Badge in Chat Header -->
  <div 
    v-else-if="callActive && !isMinimized"
    class="tw-flex tw-items-center tw-gap-2 tw-px-2 tw-py-1 tw-bg-green-50 tw-text-green-700 tw-rounded-full tw-text-xs tw-font-medium"
  >
    <div class="tw-w-2 tw-h-2 tw-bg-green-500 tw-rounded-full tw-animate-pulse"></div>
    <span>{{ callType === 'video' ? 'Video' : 'Voice' }} call active</span>
    <span class="tw-text-green-600">{{ callDuration || '00:00' }}</span>
  </div>
</template>

<script setup>
import { ref, defineProps, defineEmits, onMounted, onUnmounted, watch } from 'vue';

const props = defineProps({
  callActive: {
    type: Boolean,
    default: false
  },
  isMinimized: {
    type: Boolean,
    default: false
  },
  callType: {
    type: String,
    default: 'video'
  },
  otherParticipant: {
    type: Object,
    default: null
  },
  callStartTime: {
    type: Date,
    default: null
  },
  roomId: {
    type: String,
    default: null
  },
  conversationId: {
    type: String,
    default: null
  },
  callId: {
    type: String,
    default: null
  }
});

const emit = defineEmits(['restore-call', 'end-call']);

// Reactive data
const callDuration = ref('00:00');

// Timer for call duration
let durationTimer = null;

/**
 * Update call duration
 */
const updateCallDuration = () => {
  if (!props.callStartTime) return;
  
  const elapsed = Math.floor((new Date() - props.callStartTime) / 1000);
  const minutes = Math.floor(elapsed / 60);
  const seconds = elapsed % 60;
  callDuration.value = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

/**
 * Start duration timer
 */
const startDurationTimer = () => {
  if (durationTimer) {
    clearInterval(durationTimer);
  }
  
  // Update immediately
  updateCallDuration();
  
  // Then update every second
  durationTimer = setInterval(updateCallDuration, 1000);
};

/**
 * Stop duration timer
 */
const stopDurationTimer = () => {
  if (durationTimer) {
    clearInterval(durationTimer);
    durationTimer = null;
  }
};

/**
 * Restore the call window
 */
const restoreCall = () => {
  emit('restore-call', {
    roomId: props.roomId,
    conversationId: props.conversationId,
    callType: props.callType,
    otherParticipant: props.otherParticipant
  });
};

/**
 * End the call
 */
const endCall = () => {
  const duration = props.callStartTime 
    ? Math.floor((new Date() - props.callStartTime) / 1000) 
    : 0;
    
  emit('end-call', {
    roomId: props.roomId,
    conversationId: props.conversationId,
    callType: props.callType,
    duration,
    callId: props.callId
  });
};

// Watch for call active state changes
watch(() => props.callActive, (newActive) => {
  if (newActive && props.callStartTime) {
    startDurationTimer();
  } else {
    stopDurationTimer();
  }
});

// Watch for call start time changes
watch(() => props.callStartTime, (newStartTime) => {
  if (newStartTime && props.callActive) {
    startDurationTimer();
  }
});

// Lifecycle
onMounted(() => {
  if (props.callActive && props.callStartTime) {
    startDurationTimer();
  }
});

onUnmounted(() => {
  stopDurationTimer();
});
</script>
